import * as React from 'react'
import { connect } from 'react-redux'
import { IDetailPageState } from '../../reducers'
import * as st from './btfTabs.module.scss'
import { ref, RefmarkerTemplate } from '@amzn/dv-web-node-reftags'
import { Container } from '@amzn/dvui/dist/Container'
import * as cn from 'classnames'
import { BtfTabTreatment } from '../../reducers/features'
import { TextLocalizer } from '../localizer/localizer'
import { isEmpty, isTreatment } from '../../utils/utils'

import { CollectionTabs } from '../collectionTabs/collectionTabs'
import { Carousels } from '../carousel/carousel'
import { OtherFormats } from '../otherFormats/otherFormats'
import { CastAndCrew as TabbedCastAndCrew } from '../castAndCrew/castAndCrewTabs'
import { CastAndCrew } from '../castAndCrew/castAndCrew'
import { ScoutBanner } from '../scoutBanner/scoutBanner'
import { BtfProductMetaInfo } from '../productMetaInfo/btfProductMetaInfo'

import { showTabCollectionsSelector } from '../../selectors/collections'
import { showCarouselsSelector } from '../../selectors/carousel'
import { showOtherFormatsSelector } from '../../selectors/otherFormats'
import { showCastAndCrewSelector, showCastAndCrewTabsSelector } from '../../selectors/castAndCrew'
import { showScoutBannerSelector } from '../../selectors/scoutBanner'
import { showBtfProductDetailsSelector } from '../../selectors/productDetails'
import { isPhabletSelector } from '../../selectors/pageContext'
import { isRetailSelector } from '../../selectors/requestContext'
import { ExploreTab } from '../exploreTab/exploreTab'
import { showExploreTabSelector } from '../../selectors/exploreTab'
import { DPRecordRefTag } from '../../utils/DPRecordRefTag'
import { isStarlightEnhancedSelector } from '../../selectors/shared'

const CONTENT_ID_PREFIX = 'tab-content'
const SELECTOR_PREFIX = 'tab-selector'
const PV_NAV_ID = 'pv-nav'

const RETAIL_NAV_HEIGHT = 60
const INTERSECTION_THRESHOLD = 0.3
const FIRST_TAB = `${CONTENT_ID_PREFIX}${0}`

export interface TabsProps {
  showTabCollections: boolean
  showCarousels: boolean
  showOtherFormats: boolean
  showCastAndCrew: boolean
  showCastAndCrewTabs: boolean
  showScoutBanner: boolean
  showProductDetails: boolean
  showExploreTab: boolean
  isPhablet: boolean
  isRetail: boolean
  isStarlightEnhanced: boolean
  btfTabTreatment?: BtfTabTreatment
}

export interface TabProps {
  header: React.ReactNode
  content: React.ReactFragment
  refTag: RefmarkerTemplate
}

export interface TabsState {
  selectedTab?: string
  navIsActive: boolean
}


export class BtfTabsConnected extends React.Component<TabsProps, TabsState> {
  private navHeight: number | undefined
  private io: IntersectionObserver // this is used to select tabs on scroll
  private navIo: IntersectionObserver // this is used to make btf nav sticky
  private history: string[]
  private readonly refList: HTMLDivElement[]
  private readonly navRef: React.RefObject<HTMLDivElement>
  private isHorizontalTabbing = isTreatment(this.props.btfTabTreatment, 'T1') || this.props.isStarlightEnhanced
  private defaultTabIndex = this.props.showTabCollections && this.props.showExploreTab ? 1 : 0
  private defaultTab = `${CONTENT_ID_PREFIX}${this.defaultTabIndex}`

  constructor(props: TabsProps) {
    super(props)

    this.history = []
    this.refList = []
    this.navRef = React.createRef()

    this.state = {
      selectedTab: this.defaultTab,
      navIsActive: false
    }
  }

  componentDidMount() {
    const nav = document.getElementById(PV_NAV_ID)
    const retailNavHeight = this.props.isPhablet ? 0 : RETAIL_NAV_HEIGHT
    this.navHeight = nav && nav.getBoundingClientRect() && nav.getBoundingClientRect().height || retailNavHeight

    if (!this.isHorizontalTabbing) {
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.intersectionRatio >= INTERSECTION_THRESHOLD ?
            this.history.push(entry.target.id) :
            this.history.splice(this.history.indexOf(entry.target.id), 1);

          const lastItem = this.history.slice(-1)[0]
          if (this.state.selectedTab !== lastItem) {
            this.setState({
              selectedTab: lastItem || FIRST_TAB
            })
          }
        })
      }, {
        threshold: INTERSECTION_THRESHOLD,
      })

      if (!isEmpty(this.refList) && this.io && this.io.observe) {
        this.refList.forEach(ref => this.io.observe(ref))
      }
    }

    this.navIo = new IntersectionObserver(
      ([entry]) => {
        this.setState({
          navIsActive: !entry.isIntersecting
        })
      },
      {
        rootMargin: `-${this.navHeight}px`,
        threshold: 1
      }
    )
    if (this.navIo && this.navIo.observe && this.navRef && this.navRef.current) {
      this.navIo.observe(this.navRef.current)
    }
  }

  componentWillUnmount() {
    if (!this.isHorizontalTabbing && !isEmpty(this.refList) && this.io && this.io.unobserve) {
      this.refList.forEach(ref => this.io.unobserve(ref))
    }

    if (this.navIo && this.navIo.unobserve && this.navRef && this.navRef.current) {
      this.navIo.unobserve(this.navRef.current)
    }
  }

  moveToSelected(tabIndex: number) {
    const scrollAmount = !this.isHorizontalTabbing && tabIndex !== 0 ?
      this.refList[tabIndex].getBoundingClientRect().top - 150 :
      this.navRef.current.getBoundingClientRect().top - this.navHeight + 5

    window.scrollBy({top: Math.ceil(scrollAmount), behavior: "smooth"})
  }

  setSelectedAndScroll(tabIndex: number) {
    const selectedTab = `${CONTENT_ID_PREFIX}${tabIndex}`
    this.moveToSelected(tabIndex)
    if (!this.isHorizontalTabbing) {
      setTimeout(() => {
        if (this.state.selectedTab !== selectedTab) {
          this.setState({ selectedTab })
        }
      }, 500) // this is to give some delay to move the window to the tab
    } else {
      this.setState({ selectedTab })
    }
  }

  getTabs() {
    const { showTabCollections, showCarousels, showOtherFormats, showCastAndCrew,
            showCastAndCrewTabs, showScoutBanner, showProductDetails, showExploreTab } = this.props
    const tabType = this.isHorizontalTabbing ? 'hor' : 'ver'

    const episodesTab = showTabCollections &&
      {
        header: <TextLocalizer stringID='DV_DP_TAB_episodes' />,
        content: <CollectionTabs />,
        refTag: ref('atv_dp_btf_tab_{tabType}_ep', { tabType })
      } as TabProps

    const exploreTab = showExploreTab &&
      {
        header: <TextLocalizer stringID='DV_DP_TAB_explore' />,
        content: <ExploreTab/>,
        refTag: ref('atv_dp_btf_tab_{tabType}_exp', { tabType })
      } as TabProps

    const relatedTab = (showCarousels || showCastAndCrewTabs || showScoutBanner || showCastAndCrew) &&
      {
        header: <TextLocalizer stringID='DV_DP_TAB_related' />,
        content:
          <div className={cn({
            // Adding some margin-bottom for carousels on PV.com to make some room for hover if footer height won't be enough
            [st.relatedTabWrapper]: !this.props.isRetail && this.isHorizontalTabbing
          })}>
            <Carousels />
            <TabbedCastAndCrew />
            <ScoutBanner />
            <CastAndCrew />
          </div>,
        refTag: ref('atv_dp_btf_tab_{tabType}_rel', { tabType })
      } as TabProps

    const detailsTab = (showProductDetails || showOtherFormats) &&
      {
        header: <TextLocalizer stringID='DV_DP_TAB_details' />,
        content:
          <>
            <BtfProductMetaInfo/>
            <OtherFormats/>
          </>,
        refTag: ref('atv_dp_btf_tab_{tabType}_det', { tabType })
      } as TabProps

    return [ episodesTab, exploreTab, relatedTab, detailsTab ].filter(Boolean)
  }

  render() {
    const tabs = this.getTabs()

    if (isEmpty(tabs)) return null

    const content = tabs.map((tab, index) =>
      <>
        <input
          id={`${SELECTOR_PREFIX}${index}`}
          name='btf-content-selector'
          className={st.tabSelector}
          defaultChecked={index === this.defaultTabIndex}
          type='radio'
          onClick={() => this.setSelectedAndScroll(index)}
        />
        <div className={st.tab} id={`${CONTENT_ID_PREFIX}${index}`} ref={(r) => this.refList.push(r) } key={`btf-content-${index}`}>
          {tab.content}
        </div>
      </>
    )

    const nav = (
      <div className={cn(st.nav, {
        [st.stickyNav]: this.state.navIsActive && this.navRef.current.getBoundingClientRect().top - this.navHeight < 0, // don't show nav below the page
        [st.isPhablet]: this.props.isPhablet
      })}>
        <Container>
          <div className={st.headers}>
            {tabs.map((tab, index) =>
                <h2 key={`btf-nav-${index}`} className={st.headerWrapper}>
                  <DPRecordRefTag refTag={tab.refTag}>
                    <label className={cn(st.header, {[st.isSelected]: this.state.selectedTab === `${CONTENT_ID_PREFIX}${index}`})}
                           htmlFor={`${SELECTOR_PREFIX}${index}`}>
                      {tab.header}
                    </label>
                  </DPRecordRefTag>
                </h2>
            )}
          </div>
        </Container>
      </div>
    )

    return (
      <>
        <div ref={this.navRef} />
        {nav}
        <div className={this.isHorizontalTabbing ? st.contentWrapperTabbed : st.contentWrapper}>
          {content}
        </div>
      </>
    )
  }
}

const mapStateToProps = (state: IDetailPageState): TabsProps => ({
  showTabCollections: showTabCollectionsSelector(state),
  showCarousels: showCarouselsSelector(state),
  showOtherFormats: showOtherFormatsSelector(state),
  showCastAndCrew: showCastAndCrewSelector(state),
  showCastAndCrewTabs: showCastAndCrewTabsSelector(state),
  showScoutBanner: showScoutBannerSelector(state),
  showProductDetails: showBtfProductDetailsSelector(state),
  showExploreTab: showExploreTabSelector(state),
  isPhablet: isPhabletSelector(state),
  isRetail: isRetailSelector(state),
  isStarlightEnhanced: isStarlightEnhancedSelector(state),
  btfTabTreatment: state.features.btfTabTreatment,
})

export const BtfTabs = connect(mapStateToProps)(BtfTabsConnected)
